import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSessionLiveInfo } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/fonts.css';
import '../styles/liverace.css';

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube: watch?v= or youtu.be/ or /live/
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  // Twitch: twitch.tv/CHANNEL
  const ttMatch = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
  if (ttMatch) return `https://player.twitch.tv/?channel=${ttMatch[1]}&parent=${window.location.hostname}`;
  return null;
}

function LiveRace() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setRace(null);
    getSessionLiveInfo(id)
      .then(setRace)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Poll every 30s when there's no broadcast URL yet
  useEffect(() => {
    if (race?.broadcast_url) return;
    const interval = setInterval(() => {
      getSessionLiveInfo(id)
        .then(setRace)
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [id, race?.broadcast_url]);

  const embedUrl = race ? getEmbedUrl(race.broadcast_url) : null;
  const itelemetryUrl = race?.itelemetry_url || null;
  const trackLabel = race
    ? [race.track_name, race.config_name].filter(Boolean).join(' — ')
    : '...';

  return (
    <div className="LiveRacePage">
      <Header />
      <main>
        {/* Breadcrumb */}
        <nav className="liverace-breadcrumb">
          <Link to="/">{t('common.home')}</Link>
          {' › '}
          {race ? (
            <>
              <Link to={`/championships/${race.championship_championship_id}`}>
                {race.championship_name}
              </Link>
              {' › '}
            </>
          ) : null}
          <span>{trackLabel}</span>
        </nav>

        {/* Title */}
        <div className="liverace-title-row">
          <h1>{loading ? t('common.loading') : trackLabel}</h1>
          <span className="liverace-live-badge">{t('races.live')}</span>
        </div>

        {/* Two-column grid */}
        <div className="liverace-grid">

          {/* LEFT — Broadcast */}
          <div className="liverace-card">
            <div className="liverace-card-header">
              <h3>{t('liveRace.broadcast')}</h3>
            </div>
            <div className="liverace-broadcast-body">
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title={t('liveRace.broadcast')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="liverace-no-stream">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M15 10l4.553-2.969A1 1 0 0121 8v8a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
                  </svg>
                  <p>
                    {loading
                      ? t('common.loading')
                      : race?.broadcast_url
                        ? t('liveRace.unsupportedEmbed')
                        : t('liveRace.noBroadcast')}
                  </p>
                  {race?.broadcast_url && (
                    <a
                      href={race.broadcast_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#e8342a', fontSize: '13px' }}
                    >
                      {t('liveRace.openExternal')}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — iTelemetry or placeholder */}
          <div className="liverace-card liverace-card-timing">
            <div className="liverace-card-header">
              <h3>{t('liveRace.liveTiming')}</h3>
              {itelemetryUrl && (
                <a
                  href={itelemetryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="liverace-external-link"
                >
                  {t('liveRace.openNewTab')}
                </a>
              )}
            </div>
            {itelemetryUrl ? (
              <iframe
                src={itelemetryUrl}
                title={t('liveRace.liveTiming')}
                className="liverace-itelemetry-iframe"
                allow="autoplay"
              />
            ) : (
              <div className="liverace-tab-panel">
                <span className="liverace-wip">{t('common.comingSoon')}</span>
              </div>
            )}
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}

export default LiveRace;
